import { useEffect, useRef, useState } from 'react';
import { useBackLayer } from '@/lib/backStack';

interface Props {
  /** Подпись для чтения с экрана. */
  label: string;
  onClose: () => void;
  /** Высокое окно (88% экрана) — карточка сделки; низкое подстраивается под содержимое. */
  tall?: boolean;
  children: React.ReactNode;
}

/** Сколько нужно сдвинуть вниз, чтобы окно закрылось. */
export const CLOSE_DRAG_PX = 90;

/** Решение по окончании сдвига — отдельно от DOM, чтобы проверять. */
export function shouldClose(dy: number, velocityPxMs: number): boolean {
  return dy > CLOSE_DRAG_PX || (dy > 30 && velocityPxMs > 0.6);
}

/**
 * Нижнее окно из прототипа: затемнение, ручка, закрывается нажатием на фон, кнопкой «назад»
 * (Escape; на Андроиде — системная «назад» через стек слоёв lib/backStack)
 * и свайпом вниз за ручку/заголовок. Содержимое внутри прокручивается отдельно (.sb), поэтому свайп
 * внутри списка окно не закрывает.
 */
export function Sheet({ label, onClose, tall, children }: Props) {
  const [dy, setDy] = useState(0);
  const drag = useRef<{ y: number; t: number } | null>(null);
  useBackLayer(onClose);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const down = (e: React.PointerEvent) => {
    drag.current = { y: e.clientY, t: Date.now() };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const move = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setDy(Math.max(0, e.clientY - drag.current.y));
  };
  const up = (e: React.PointerEvent) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    const delta = Math.max(0, e.clientY - d.y);
    const v = delta / Math.max(1, Date.now() - d.t);
    if (shouldClose(delta, v)) onClose();
    else setDy(0);
  };

  return (
    <>
      <div className="back" onClick={onClose} aria-hidden />
      <div
        className={`sheet${tall ? ' tall' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        style={dy ? { transform: `translateY(${dy}px)`, transition: 'none' } : undefined}
      >
        <div className="sheet-drag" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
          <div className="grab" />
        </div>
        {children}
      </div>
    </>
  );
}
